import { Card, CardContent } from "../ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "../ui/dropdown-menu";
import {
  Plus, Users, Cake, Send, Loader2, MoreVertical, ShoppingBag, Gift, Trash2,
  Search, History, Edit3, Filter, X, Mail, Phone,
} from "lucide-react";
import { useMemo, useState } from "react";
import CustomerTransactionsDialog from "./CustomerTransactionsDialog";
import AdjustPointsDialog from "./AdjustPointsDialog";

function formatCurrency(v) { return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(v); }
function formatDate(d) { return new Date(d).toLocaleDateString("es-ES", { day: "2-digit", month: "short" }); }
function formatBirthday(b) {
  if (!b) return "";
  const [y, m, d] = b.split("-");
  return `${d}/${m}`;
}
function isBirthdayToday(c) {
  if (!c.birthday) return false;
  const today = new Date();
  const [, m, d] = c.birthday.split("-");
  return parseInt(m) === today.getMonth() + 1 && parseInt(d) === today.getDate();
}

export function CustomersTab({
  customers, birthdayCustomers, configForm, sendingBirthday, levels = [],
  onAddCustomer, onSendBirthday, onRegisterPurchase, onRedeemReward, onDeleteCustomer,
  onPointsAdjusted,
  getCustomerLevel, getNextLevel,
}) {
  const [search, setSearch] = useState("");
  const [filterLevel, setFilterLevel] = useState("all");
  const [chips, setChips] = useState({
    has_email: false,
    has_phone: false,
    birthday_this_month: false,
    inactive_30d: false,
    has_points: false,
  });
  const [txCustomer, setTxCustomer] = useState(null);
  const [adjustCustomer, setAdjustCustomer] = useState(null);

  const toggleChip = (k) => setChips((c) => ({ ...c, [k]: !c[k] }));
  const activeChips = Object.values(chips).filter(Boolean).length;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 3600 * 1000);
    const thisMonth = now.getMonth() + 1; // 1-12

    return customers.filter((c) => {
      if (q) {
        const hay = [c.name, c.email, c.phone].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (chips.has_email && !c.email) return false;
      if (chips.has_phone && !c.phone) return false;
      if (chips.has_points && !(c.points > 0)) return false;
      if (chips.birthday_this_month) {
        if (!c.birthday) return false;
        const bm = parseInt(c.birthday.split("-")[1], 10);
        if (bm !== thisMonth) return false;
      }
      if (chips.inactive_30d) {
        const lv = c.last_visit ? new Date(c.last_visit) : null;
        if (lv && lv > thirtyDaysAgo) return false;
      }
      if (filterLevel !== "all") {
        const l = getCustomerLevel(c.points || 0);
        if (!l || l.name !== filterLevel) return false;
      }
      return true;
    });
  }, [customers, search, chips, filterLevel, getCustomerLevel]);

  const resetFilters = () => {
    setSearch(""); setFilterLevel("all");
    setChips({ has_email: false, has_phone: false, birthday_this_month: false, inactive_30d: false, has_points: false });
  };

  return (
    <div>
      {/* Birthday banner */}
      {birthdayCustomers.length > 0 && (
        <Card className="border-none shadow-soft mb-6 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20" data-testid="birthday-banner">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
                <Cake className="w-5 h-5 text-amber-700" />
              </div>
              <div>
                <p className="font-bold text-sm">Cumpleanos hoy</p>
                <p className="text-xs text-muted-foreground">
                  {birthdayCustomers.length} cliente{birthdayCustomers.length > 1 ? "s" : ""} cumple{birthdayCustomers.length > 1 ? "n" : ""} anos hoy
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              {birthdayCustomers.map(c => (
                <div key={c.customer_id} className="flex items-center gap-2 bg-card dark:bg-gray-900 rounded-xl px-3 py-2 shadow-sm border" data-testid={`birthday-customer-${c.customer_id}`}>
                  <Cake className="w-4 h-4 text-amber-500 shrink-0" />
                  <span className="font-medium text-sm">{c.name}</span>
                  {configForm.birthday_discount_enabled && (
                    <Button size="sm" variant="outline"
                      className="h-7 text-xs gap-1 border-amber-300 text-amber-700 hover:bg-amber-50"
                      onClick={() => onSendBirthday(c.customer_id)}
                      disabled={sendingBirthday[c.customer_id]}
                      data-testid={`send-birthday-discount-${c.customer_id}`}>
                      {sendingBirthday[c.customer_id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                      Enviar descuento
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {customers.length === 0 ? (
        <Card className="border-dashed border-2">
          <CardContent className="py-16 text-center">
            <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="font-heading text-lg font-semibold mb-2">Sin clientes</h3>
            <p className="text-muted-foreground mb-6">Anade clientes o comparte el QR para que se registren solos</p>
            <Button onClick={onAddCustomer}><Plus className="w-4 h-4 mr-2" /> Anadir cliente</Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-none shadow-soft">
          <CardContent className="p-0">
            {/* Filter bar */}
            <div className="flex flex-col gap-3 p-4 border-b border-border" data-testid="customers-filter-bar">
              <div className="flex flex-wrap gap-2 items-center">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar por nombre, email o teléfono…"
                    className="pl-9"
                    data-testid="customers-search-input"
                  />
                </div>
                {levels?.length > 0 && (
                  <select
                    value={filterLevel}
                    onChange={(e) => setFilterLevel(e.target.value)}
                    className="border border-border bg-background rounded-md px-3 py-2 text-sm"
                    data-testid="customers-level-filter"
                  >
                    <option value="all">Todos los niveles</option>
                    {levels.map((l) => (<option key={l.name} value={l.name}>{l.name}</option>))}
                  </select>
                )}
                {(activeChips > 0 || search || filterLevel !== "all") && (
                  <Button variant="ghost" size="sm" onClick={resetFilters} data-testid="customers-reset-filters">
                    <X className="w-3.5 h-3.5 mr-1" /> Limpiar
                  </Button>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {[
                  { k: "has_email",           label: "Con email",           Icon: Mail },
                  { k: "has_phone",           label: "Con teléfono",        Icon: Phone },
                  { k: "birthday_this_month", label: "Cumple este mes",     Icon: Cake },
                  { k: "inactive_30d",        label: "Inactivos > 30 días", Icon: Filter },
                  { k: "has_points",          label: "Con puntos",          Icon: Gift },
                ].map(({ k, label, Icon }) => (
                  <button
                    key={k}
                    onClick={() => toggleChip(k)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition ${
                      chips[k]
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground hover:bg-muted/70"
                    }`}
                    data-testid={`customers-chip-${k}`}
                  >
                    <Icon className="w-3 h-3" /> {label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground" data-testid="customers-filter-count">
                Mostrando {filtered.length} de {customers.length} cliente{customers.length === 1 ? "" : "s"}
              </p>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Contacto</TableHead>
                  <TableHead>Cumpleanos</TableHead>
                  <TableHead>Nivel</TableHead>
                  <TableHead>Puntos</TableHead>
                  <TableHead>Total gastado</TableHead>
                  <TableHead>Ultima visita</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground text-sm">
                      No hay clientes que coincidan con los filtros actuales.
                    </TableCell>
                  </TableRow>
                ) : null}
                {filtered.map((c) => {
                  const level = getCustomerLevel(c.points || 0);
                  const next = getNextLevel(c.points || 0);
                  return (
                    <TableRow key={c.customer_id} data-testid={`customer-row-${c.customer_id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{c.name}</p>
                          <p className="text-xs text-muted-foreground">{c.total_visits} visitas</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {c.email && <p>{c.email}</p>}
                          {c.phone && <p className="text-muted-foreground">{c.phone}</p>}
                        </div>
                      </TableCell>
                      <TableCell>
                        {c.birthday ? (
                          <div className="flex items-center gap-1.5">
                            {isBirthdayToday(c) && <Cake className="w-4 h-4 text-amber-500" />}
                            <span className={`text-sm ${isBirthdayToday(c) ? "font-semibold text-amber-700" : "text-muted-foreground"}`}>
                              {formatBirthday(c.birthday)}
                            </span>
                          </div>
                        ) : <span className="text-muted-foreground text-sm">-</span>}
                      </TableCell>
                      <TableCell>
                        {level
                          ? <Badge className="text-xs border-0" style={{ background: level.color + "30", color: level.color }}>{level.name}</Badge>
                          : <span className="text-muted-foreground text-sm">-</span>}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
                          {(c.points || 0).toLocaleString()} pts
                        </Badge>
                        {next && <p className="text-xs text-muted-foreground mt-1">{next.min_points - (c.points || 0)} pts para {next.name}</p>}
                      </TableCell>
                      <TableCell className="font-mono">{formatCurrency(c.total_spent || 0)}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">{c.last_visit ? formatDate(c.last_visit) : "-"}</TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`customer-actions-${c.customer_id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => onRegisterPurchase(c)} data-testid={`action-purchase-${c.customer_id}`}>
                              <ShoppingBag className="w-4 h-4 mr-2" /> Registrar consumo
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => onRedeemReward(c)} data-testid={`action-redeem-${c.customer_id}`}>
                              <Gift className="w-4 h-4 mr-2" /> Canjear recompensa
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setAdjustCustomer(c)} data-testid={`action-adjust-${c.customer_id}`}>
                              <Edit3 className="w-4 h-4 mr-2" /> Ajustar puntos (manual)
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setTxCustomer(c)} data-testid={`action-history-${c.customer_id}`}>
                              <History className="w-4 h-4 mr-2" /> Ver historial
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-destructive" onClick={() => onDeleteCustomer(c.customer_id)} data-testid={`action-delete-${c.customer_id}`}>
                              <Trash2 className="w-4 h-4 mr-2" /> Eliminar
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <CustomerTransactionsDialog
        open={!!txCustomer}
        onClose={() => setTxCustomer(null)}
        customer={txCustomer}
      />
      <AdjustPointsDialog
        open={!!adjustCustomer}
        onClose={() => setAdjustCustomer(null)}
        customer={adjustCustomer}
        onSuccess={() => { onPointsAdjusted?.(); }}
      />
    </div>
  );
}
